import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';
import { getDDQSections, DDQSection } from '../data/ddqQuestions';

interface DDQ {
  id: string;
  firm_id: string;
  fund_id: string | null;
  type: string;
  asset_class: string;
  status: string;
  allow_pdf: boolean;
}

interface Answer {
  section_key: string;
  question_key: string;
  answer_text: string;
}

interface Question {
  id: string;
  section_key: string;
  question_text: string;
  answer_text: string | null;
  asked_at: string;
  answered_at: string | null;
}

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  real_estate: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

export default function DDQView() {
  const { ddqId } = useParams();
  const navigate = useNavigate();
  const { firm, profile } = useProfile();
  const [ddq, setDdq] = useState<DDQ | null>(null);
  const [answers, setAnswers] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<DDQSection[]>([]);
  const [firmName, setFirmName] = useState('');
  const [fundName, setFundName] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [activeSection, setActiveSection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newQuestion, setNewQuestion] = useState('');
  const [askingSection, setAskingSection] = useState<string | null>(null);
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [activeTab, setActiveTab] = useState<'ddq' | 'questions'>('ddq');

  useEffect(() => {
    if (!ddqId || !firm) return;
    fetchData();
  }, [ddqId, firm]);

  const fetchData = async () => {
    const { data: ddqData } = await supabase
      .from('ddqs').select('*').eq('id', ddqId).single();
    if (!ddqData) { setLoading(false); return; }
    setDdq(ddqData);

    const s = getDDQSections(ddqData.asset_class, ddqData.type as 'firm' | 'fund');
    setSections(s);

    // Get firm name
    const { data: firmData } = await supabase
      .from('firms').select('name').eq('id', ddqData.firm_id).single();
    if (firmData) setFirmName(firmData.name);

    // Get fund name if fund DDQ
    if (ddqData.fund_id) {
      const { data: fundData } = await supabase
        .from('funds').select('name').eq('id', ddqData.fund_id).single();
      if (fundData) setFundName(fundData.name);
    }

    // Get answers
    const { data: answerData } = await supabase
      .from('ddq_answers').select('*').eq('ddq_id', ddqId);
    if (answerData) {
      const grouped: Record<string, Record<string, string>> = {};
      answerData.forEach((a: any) => {
        if (!grouped[a.section_key]) grouped[a.section_key] = {};
        grouped[a.section_key][a.question_key] = a.answer_text || '';
      });
      setAnswers(grouped);
    }

    // Get this allocator's questions
    if (firm) {
      const { data: questionData } = await supabase
        .from('ddq_questions').select('*')
        .eq('ddq_id', ddqId)
        .eq('allocator_firm_id', firm.id)
        .order('asked_at', { ascending: true });
      if (questionData) setQuestions(questionData);
    }

    setLoading(false);
  };

  const handleAskQuestion = async () => {
    if (!newQuestion.trim() || !firm || !ddqId) return;
    setSubmittingQuestion(true);

    await supabase.from('ddq_questions').insert({
      ddq_id: ddqId,
      allocator_firm_id: firm.id,
      section_key: askingSection,
      question_text: newQuestion.trim(),
    });

    setNewQuestion('');
    setAskingSection(null);
    setSubmittingQuestion(false);
    fetchData();
  };

  if (loading) return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#6B7280' }}>Loading DDQ...</div>
    </div>
  );

  if (!ddq) return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}>
      <div style={{ color: '#C0222A' }}>DDQ not found or access not granted.</div>
    </div>
  );

  const currentSection = sections[activeSection];
  const unansweredQuestions = questions.filter(q => !q.answered_at).length;
  const answeredQuestions = questions.filter(q => q.answered_at).length;

  return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}>
      <div style={{ maxWidth: '860px', margin: '0 auto' }}>

        {/* Header */}
        <button onClick={() => navigate(-1)} style={{
          background: 'transparent', border: '1px solid #D0D5DD',
          borderRadius: '4px', color: '#6B7280', padding: '6px 12px',
          cursor: 'pointer', fontSize: '12px', marginBottom: '24px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>← Back</button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '24px', color: '#0A0A0A', marginBottom: '4px' }}>
              {ddq.type === 'firm' ? `${firmName}, Firm DDQ` : `${fundName}, Fund DDQ`}
            </h1>
            <p style={{ fontSize: '13px', color: '#6B7280' }}>
              {classLabels[ddq.asset_class]} · {firmName}
            </p>
          </div>
          {ddq.allow_pdf && (
            <button
              onClick={() => window.print()}
              style={{
                background: '#FFFFFF', border: '1px solid #D0D5DD',
                borderRadius: '3px', color: '#0D6E8A', fontSize: '13px',
                padding: '8px 16px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            >↓ Download PDF</button>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '2px', background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '4px', width: 'fit-content', marginBottom: '24px' }}>
          {[
            { key: 'ddq', label: 'DDQ Responses' },
            { key: 'questions', label: `My Questions${questions.length > 0 ? ` (${questions.length})` : ''}` },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key as any)} style={{
              padding: '6px 16px', borderRadius: '3px', fontSize: '13px',
              cursor: 'pointer', border: 'none',
              background: activeTab === tab.key ? '#0A1628' : 'transparent',
              color: activeTab === tab.key ? '#FFFFFF' : '#6B7280',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}>{tab.label}</button>
          ))}
        </div>

        {activeTab === 'ddq' && (
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '20px' }}>
            {/* Section nav */}
            <div style={{ position: 'sticky', top: '20px', alignSelf: 'start' }}>
              {sections.map((s, i) => {
                const sectionAnswers = answers[s.key] || {};
                const answered = Object.values(sectionAnswers).filter(v => v).length;
                const total = s.questions.length;
                const complete = answered === total;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActiveSection(i)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '10px 14px', borderRadius: '4px', marginBottom: '4px',
                      cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                      border: `1px solid ${i === activeSection ? '#C9A84C' : 'transparent'}`,
                      background: i === activeSection ? '#FDF6E7' : 'transparent',
                      color: i === activeSection ? '#C9A84C' : '#6B7280',
                      fontSize: '12px',
                    }}
                  >
                    <div style={{ marginBottom: '3px' }}>{s.title}</div>
                    <div style={{ fontSize: '10px', color: complete ? '#0D6E8A' : '#6B7280' }}>
                      {answered}/{total} answered
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Section content */}
            {currentSection && (
              <div>
                <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '28px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                    <div>
                      <h2 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '20px', color: '#0A0A0A', marginBottom: '6px' }}>
                        {currentSection.title}
                      </h2>
                      <p style={{ fontSize: '13px', color: '#6B7280' }}>{currentSection.description}</p>
                    </div>
                    <button
                      onClick={() => setAskingSection(currentSection.key)}
                      style={{
                        background: '#FFFFFF', border: '1px solid #D0D5DD',
                        borderRadius: '3px', color: '#0D6E8A', fontSize: '12px',
                        padding: '6px 14px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                        whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '16px'
                      }}
                    >+ Ask question</button>
                  </div>

                  {currentSection.questions.map(q => {
                    const answer = answers[currentSection.key]?.[q.key];
                    return (
                      <div key={q.key} style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #E8EBF0' }}>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>{q.label}</div>
                        <div style={{ fontSize: '14px', color: answer ? '#0A0A0A' : '#9CA3AF', lineHeight: 1.7 }}>
                          {answer || 'Not provided'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Navigation */}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button
                    onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                    disabled={activeSection === 0}
                    style={{
                      background: '#FFFFFF', border: '1px solid #D0D5DD',
                      borderRadius: '3px', color: activeSection === 0 ? '#9CA3AF' : '#3D4552',
                      padding: '9px 18px', cursor: activeSection === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '13px', fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}
                  >← Previous</button>
                  <button
                    onClick={() => setActiveSection(Math.min(sections.length - 1, activeSection + 1))}
                    disabled={activeSection === sections.length - 1}
                    style={{
                      background: activeSection === sections.length - 1 ? '#FFFFFF' : '#0A1628',
                      border: activeSection === sections.length - 1 ? '1px solid #D0D5DD' : 'none',
                      borderRadius: '3px',
                      color: activeSection === sections.length - 1 ? '#9CA3AF' : '#FFFFFF',
                      padding: '9px 18px',
                      cursor: activeSection === sections.length - 1 ? 'not-allowed' : 'pointer',
                      fontSize: '13px', fontWeight: 600, fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}
                  >Next →</button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'questions' && (
          <div>
            {/* Ask a general question */}
            <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '20px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '10px' }}>
                Ask a question
              </div>
              <textarea
                value={askingSection === 'general' ? newQuestion : ''}
                onChange={e => { setAskingSection('general'); setNewQuestion(e.target.value); }}
                placeholder="Ask the manager a question about this DDQ..."
                rows={3}
                style={{
                  width: '100%', background: '#FFFFFF', border: '1px solid #D0D5DD',
                  borderRadius: '3px', color: '#0A0A0A', fontSize: '13px', padding: '10px 14px',
                  outline: 'none', fontFamily: 'system-ui, -apple-system, sans-serif', resize: 'vertical',
                  boxSizing: 'border-box', lineHeight: 1.6, marginBottom: '10px'
                }}
              />
              <button
                onClick={handleAskQuestion}
                disabled={!newQuestion.trim() || submittingQuestion}
                style={{
                  background: newQuestion.trim() ? '#0A1628' : '#E8EBF0',
                  border: 'none', borderRadius: '3px',
                  color: newQuestion.trim() ? '#FFFFFF' : '#9CA3AF',
                  fontSize: '13px', fontWeight: 600, padding: '9px 20px',
                  cursor: newQuestion.trim() ? 'pointer' : 'not-allowed',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
              >{submittingQuestion ? 'Sending...' : 'Send question'}</button>
            </div>

            {/* Q&A thread */}
            {questions.length === 0 ? (
              <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '40px', textAlign: 'center' }}>
                <div style={{ fontSize: '13px', color: '#6B7280' }}>No questions yet, ask the manager anything about this DDQ</div>
              </div>
            ) : (
              questions.map(q => (
                <div key={q.id} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '18px 20px', marginBottom: '10px' }}>
                  <div style={{ marginBottom: q.answer_text ? '14px' : '0' }}>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '5px' }}>
                      You · {q.section_key && q.section_key !== 'general' ? `${q.section_key.replace(/_/g, ' ')} · ` : ''}{new Date(q.asked_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                    <div style={{ fontSize: '14px', color: '#0A0A0A' }}>{q.question_text}</div>
                  </div>

                  {q.answer_text ? (
                    <div style={{ background: '#F5F6F8', borderRadius: '4px', padding: '12px 14px', borderLeft: '3px solid #0D6E8A' }}>
                      <div style={{ fontSize: '11px', color: '#0D6E8A', marginBottom: '5px' }}>
                        Manager · {q.answered_at ? new Date(q.answered_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                      </div>
                      <div style={{ fontSize: '14px', color: '#0A0A0A', lineHeight: 1.6 }}>{q.answer_text}</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#92640A', marginTop: '8px' }}>
                      ⏳ Awaiting response from manager
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Ask question modal */}
        {askingSection && askingSection !== 'general' && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              background: '#FFFFFF', border: '1px solid #D0D5DD',
              borderRadius: '4px', padding: '28px', width: '480px', maxWidth: '90vw'
            }}>
              <h3 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '18px', color: '#0A0A0A', marginBottom: '6px' }}>
                Ask a question
              </h3>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '16px' }}>
                Section: {askingSection.replace(/_/g, ' ')}
              </p>
              <textarea
                value={newQuestion}
                onChange={e => setNewQuestion(e.target.value)}
                placeholder="Type your question..."
                rows={4}
                autoFocus
                style={{
                  width: '100%', background: '#FFFFFF', border: '1px solid #D0D5DD',
                  borderRadius: '3px', color: '#0A0A0A', fontSize: '13px', padding: '10px 14px',
                  outline: 'none', fontFamily: 'system-ui, -apple-system, sans-serif', resize: 'vertical',
                  boxSizing: 'border-box', lineHeight: 1.6, marginBottom: '14px'
                }}
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => { setAskingSection(null); setNewQuestion(''); }} style={{
                  flex: 1, background: '#FFFFFF', border: '1px solid #D0D5DD',
                  borderRadius: '3px', color: '#3D4552', padding: '10px',
                  cursor: 'pointer', fontSize: '13px', fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>Cancel</button>
                <button
                  onClick={handleAskQuestion}
                  disabled={!newQuestion.trim() || submittingQuestion}
                  style={{
                    flex: 1, background: '#0A1628', border: 'none', borderRadius: '3px',
                    color: '#FFFFFF', padding: '10px', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 600, fontFamily: 'system-ui, -apple-system, sans-serif',
                    opacity: !newQuestion.trim() ? 0.5 : 1
                  }}
                >{submittingQuestion ? 'Sending...' : 'Send question'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
